import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import UIPageTitle from '@/components/shared/UIPageTitle';
import UIButton from '@/components/shared/UIButton';
import UIIconButton from '@/components/shared/UIIconButton';
import { profileSelector } from '@/store/profile/profileSlices';
import { type IProperty } from '@/types';
import './PropertyPageHeader.scss';

export interface PropertyPageHeaderProps {
  /**
   * Об'єкт нерухомості
   */
  property?: IProperty | null;

  /**
   * ID нерухомості для навігації
   */
  propertyId?: string | number;

  /**
   * Стан завантаження
   */
  loading?: boolean;

  /**
   * Додатковий CSS клас
   */
  className?: string;

  /**
   * Обробник кліку по кнопці "Back"
   */
  onBackClick?: () => void;

  /**
   * Обробник кліку по кнопці "Edit"
   */
  onEditClick?: () => void;

  /**
   * Показувати кнопку "Edit"
   */
  showEditButton?: boolean;
}

const baseClass = 'PropertyPageHeader';

const PropertyPageHeader: React.FC<PropertyPageHeaderProps> = ({
                                                                 property,
                                                                 propertyId,
                                                                 loading = false,
                                                                 className = '',
                                                                 onBackClick,
                                                                 onEditClick,
                                                                 showEditButton = true,
                                                               }) => {
  const navigate = useNavigate();
  const profile = useSelector(profileSelector);

  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    } else {
      window.history.back(); // Changed to go back in history
    }
  };

  const handleEditClick = () => {
    if (onEditClick) {
      onEditClick();
    } else {
      navigate(`/dashboard/properties/${propertyId}/edit`);
    }
  };

  // Заголовок з property name
  const titleText = property?.name
    ? `Property: ${property.name}`
    : 'Property';

  // Кнопки дій для власника
  const renderActions = () => {
    // Показуємо кнопки тільки якщо користувач є власником
    const isOwner = profile?.id === property?.owner?.id;

    if (!isOwner) {
      return null;
    }

    return (
      <div className={`${baseClass}__actions`}>
        <UIButton
          variant="secondary"
          outline
          size="medium"
          onClick={handleBackClick}
          disabled={loading}
        >
          Back
        </UIButton>

        {showEditButton && (
          <UIIconButton
            variant="primary"
            size="medium"
            iconType="edit"
            text="Edit"
            onClick={handleEditClick}
            disabled={loading}
          />
        )}
      </div>
    );
  };

  return (
    <div className={`${baseClass} ${className}`.trim()}>
      <UIPageTitle
        title={titleText}
        action={renderActions()}
        showDivider={true}
        size="medium"
        variant="default"
        className={`${baseClass}__title`}
      />
    </div>
  );
};

export default PropertyPageHeader;